<template>
  <div v-if="block">
    <menu-content-container
      :chip="$t('forms.loop_properties')"
      chip-secondary
      border-full
    >
      <content-row class="!block">
        <MTDropdownWithSearch
          v-model="forMargetag"
          :label="$t('forms.for_merge_tag')"
          :placeholder="$t('forms.select_merge_tag')"
          :flat-merge-tags="loopMergetags"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
          data-testid="for-merge-tag-dropdown"
        />
      </content-row>
      <content-row>
        <TButtonGroup :label="$t('forms.block_layout')">
          <TGroupButton
            :tooltip="$t('forms.layout_vertically')"
            data="imgText"
            data-testid="image-text"
            small
            :active="layout === 'row'"
            @button-clicked="layout = 'row'"
          >
            <LayoutImgText />
          </TGroupButton>
          <TGroupButton
            :tooltip="$t('forms.layout_horizontally')"
            data="oneColumn"
            data-testid="column"
            small
            :active="layout === 'column'"
            @button-clicked="layout = 'column'"
          >
            <LayoutOneColumn />
          </TGroupButton>
        </TButtonGroup>

        <t-input-number
          v-if="layout !== 'column'"
          v-model.number="colsCount"
          data-testid="loop-cols-input"
          :label="$t('forms.section_cols')"
          unit=""
          :min="1"
          :max="8"
          regex-pattern="[1-8]{1}"
        ></t-input-number>
        <t-input-number
          v-else
          v-model.number="maxPerRow"
          data-testid="loop-max-per-row-input"
          :label="$t('forms.max_per_row')"
          unit=""
          :min="1"
          :max="4"
          regex-pattern="[1-4]{1}"
        ></t-input-number>
      </content-row>
      <content-row class="!mt-3">
        <t-switch
          v-model="stuckOnMobile"
          data-testid="do-not-stack-on-mobile-switch"
          :label="$t('forms.stuck')"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full>
      <content-row>
        <color-picker
          :label="$t('forms.section_bgc')"
          data-testid="loop-background-color"
          position="left"
          :default-color="bgColor"
          @color-change="bgColor = $event"
        ></color-picker>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TButton
          v-if="!gradient"
          data-testid="add-gradient"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
        <TTabsToggle
          v-if="gradient"
          :header="$t('forms.gradient')"
          default-open
          no-move
          show-remove
          @remove-block="gradient = undefined"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
      <content-row v-if="colsCount > 1">
        <ColumnsWidth />
      </content-row>
    </menu-content-container>

    <padding-form data-testid="loop-margin"></padding-form>
    <menu-content-container
      :header="$t('forms.column_properties')"
      :padding-left-right="8"
      :margin-top="15"
      border-full
    >
      <VueDraggable
        :model-value="cols"
        class="mb-5 space-y-2"
        :animation="150"
        group="columns"
        item-key="network"
        handle=".drag-handle"
        @update="onNewtworkDragEnd"
      >
        <TTabsToggle
          v-for="(col, index) in cols"
          :key="index"
          :header="`${$t('forms.column')} ${index + 1}`"
          :data-testid="`column-${index}-tab`"
        >
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 5.25C3.15625 5.25 2.5 5.92969 2.5 6.75V14.25C2.5 15.0938 3.15625 15.75 4 15.75H16C16.8203 15.75 17.5 15.0938 17.5 14.25V6.75C17.5 5.92969 16.8203 5.25 16 5.25H4ZM7 6.75V14.25H4V6.75H7ZM8.5 6.75H11.5V14.25H8.5V6.75ZM16 6.75V14.25H13V6.75H16Z"
                fill="currentColor"
              />
            </svg>
          </template>
          <columnSettings :column-index="index" block-column></columnSettings>
        </TTabsToggle>
      </VueDraggable>
    </menu-content-container>

    <menu-content-container
      v-if="forMargetag"
      :header="$t('forms.children_properties')"
      :padding-left-right="8"
      :margin-top="15"
      border-full
    >
      <div class="space-y-2">
        <TTabsToggle
          v-for="childBlock in blockChildren"
          :key="childBlock.block.uid"
          :header="getBlockName(childBlock.block)"
          :data-testid="`child-block-${childBlock.block.uid}`"
          :default-displayed="!childBlock.block.hidden"
          toggle-show
          no-move
          @hide-block="hideBlock(childBlock.block)"
          @show-block="showBlock(childBlock.block)"
        >
          <template #icon>
            <ChildSvg
              :block-type="childBlock.block.tagName"
              class="h-6 max-w-full"
            />
          </template>
          <ButtonForm
            v-if="childBlock.block.tagName === 'mj-button'"
            :block="childBlock.block"
            :col-index="childBlock.colIndex"
            :block-index="childBlock.blockIndex"
          />
          <ImageForm
            v-else-if="childBlock.block.tagName === 'mj-image'"
            :block="childBlock.block"
            :col-index="childBlock.colIndex"
            :block-index="childBlock.blockIndex"
          />
          <TextForm
            v-else
            :block="childBlock.block"
            :col-index="childBlock.colIndex"
            :block-index="childBlock.blockIndex"
          />
        </TTabsToggle>
      </div>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import ColumnsWidth from "./loopBlock/columnsWidth.vue";
  import ButtonForm from "./loopBlock/buttonForm.vue";
  import ImageForm from "./loopBlock/imageForm.vue";
  import TextForm from "./loopBlock/textForm.vue";
  import ChildSvg from "./loopBlock/childSvg.vue";

  import MTDropdownWithSearch from "./components/MTDropdownWithSearch/MTDropdownWithSearch.vue";
  import colorPicker from "./elements/colorPicker.vue";
  import columnSettings from "./elements/columnSettings.vue";
  import PaddingForm from "./paddingForm.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";

  import { IMergeTag } from "@/schemas/mergeTags/mergeTagsSchema";
  import { TopolLoopBlock } from "@/schemas/template/blocks/loopBlockSchema";

  import { LayoutImgText, LayoutOneColumn } from "@topol/icons";
  import {
    TTabsToggle,
    TInputNumber,
    TButton,
    TNote,
    TButtonGroup,
    TGroupButton,
    TSwitch,
  } from "@topol/twig-ui";

  import { VueDraggable } from "vue-draggable-plus";
  import { computed } from "vue";

  const mjmlStore = useMjmlStore();
  const mergeTagsStore = useMergeTagsStore();

  const block = computed(() => {
    return mjmlStore.getActiveBlock as TopolLoopBlock;
  });

  const forMargetag = computed({
    get() {
      return block.value.forMergeTag;
    },
    set(val: IMergeTag) {
      if (JSON.stringify(val) === JSON.stringify(forMargetag.value)) {
        return;
      }
      mjmlStore.setActiveLoopMergetag(val);
    },
  });

  const loopMergetags = computed(() => {
    return mergeTagsStore.flatMergeTagsWithArray.filter(
      (tag) => tag.childrenProperties && tag.childrenProperties.length > 0
    );
  });

  const layout = computed({
    get() {
      return block.value.layout || "row";
    },
    set(val: string) {
      if (val === "column") {
        colsCount.value = 1;
      }
      mjmlStore.updateBlockProp({
        prop: "layout",
        value: val,
      });
    },
  });

  const bgColor = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("background-color");
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        value: val,
        prop: "background-color",
      });
    },
  });

  const gradient = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("gradient");
    },
    set(val?: string) {
      mjmlStore.updateBlockAttribute({
        prop: "gradient",
        value: val,
      });
    },
  });

  const colsCount = computed({
    get() {
      return mjmlStore.getActiveBlockColumnsCount;
    },
    set(value: number) {
      if (value < 1 || value > 8) {
        return;
      }

      if (value === colsCount.value) {
        return;
      }

      if (value < colsCount.value) {
        for (let i = colsCount.value; i > value; i--) {
          mjmlStore.removeBlockColumn();
        }
      } else {
        for (let i = colsCount.value; i < value; i++) {
          mjmlStore.addBlockColumn();
        }
      }
    },
  });

  const maxPerRow = computed({
    get() {
      return block.value.maxPerRow || 1;
    },
    set(value: number) {
      if (value < 1 || value > 4) {
        return;
      }

      mjmlStore.updateBlockProp({
        prop: "maxPerRow",
        value,
      });
    },
  });

  const doNotStuck = computed({
    get() {
      if (mjmlStore.getActiveBlockAttrString("in-group")) {
        return mjmlStore.getActiveBlockAttrString("in-group");
      }
      return false;
    },
    set(val: boolean) {
      mjmlStore.updateBlockAttribute({
        prop: "in-group",
        value: val,
      });
    },
  });

  const stuckOnMobile = computed({
    get() {
      return !doNotStuck.value;
    },
    set(val: boolean) {
      doNotStuck.value = !val;
    },
  });

  const cols = computed(() => {
    return block.value.children;
  });

  const blockChildren = computed(() => {
    const children: {
      colIndex: number;
      blockIndex: number;
      block: TopolLoopBlock["children"][0]["children"][0];
    }[] = [];

    for (let i = 0; i < cols.value.length; i++) {
      const col = cols.value[i];
      for (let j = 0; j < col.children.length; j++) {
        const block = col.children[j];
        children.push({
          colIndex: i,
          blockIndex: j,
          block,
        });
      }
    }

    // children.sort((a, b) => {
    //   return (a.block.hidden ? 1 : 0) - (b.block.hidden ? 1 : 0);
    // });
    return children;
  });

  function getBlockName(block: TopolLoopBlock["children"][0]["children"][0]) {
    if (!forMargetag.value) {
      return block.tagName;
    }
    switch (block.tagName) {
      case "mj-text":
        return (
          forMargetag.value.childrenProperties?.find(
            (item) => item.value === block.content
          )?.label || block.content
        );
      case "mj-image":
        return (
          forMargetag.value.childrenProperties?.find(
            (item) => item.value === block.attributes.src
          )?.label || block.attributes.src
        );
      case "mj-button":
        return (
          forMargetag.value.childrenProperties?.find(
            (item) => item.value === block.attributes.href
          )?.label || block.attributes.href
        );
      default:
        //@ts-expect-error
        return block.tagName;
    }
  }

  function onNewtworkDragEnd(evt: {
    oldIndex: number | undefined;
    newIndex: number | undefined;
  }) {
    mjmlStore.reorderLoopColumns({
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex,
    });
  }

  function hideBlock(block: TopolLoopBlock["children"][0]["children"][0]) {
    mjmlStore.takeSnapshot();
    block.hidden = true;
  }

  function showBlock(block: TopolLoopBlock["children"][0]["children"][0]) {
    mjmlStore.takeSnapshot();
    block.hidden = false;
  }

  function addGradient() {
    gradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }
</script>
